/**
 * useFooterAgentInfo — public-safe agent + brokerage profile for
 * the site footer.
 *
 * Backed by GET /agent-config/public. The endpoint is anonymous-
 * accessible so we don't gate it on auth, and we share a single
 * module-level promise across all consumers so opening N pages in
 * one session triggers exactly one network round-trip.
 *
 * All fields are nullable strings; the Footer falls back to empty
 * strings (renders the row blank rather than "undefined").
 */
import { useEffect, useState } from 'react';
import { apiClient } from '@/shared/api';

export interface FooterAgentInfo {
    agent_name: string | null;
    agent_title: string | null;
    agent_phone: string | null;
    agent_license: string | null;
    /** The brokerage as Settings → Agent Profile names it (the /contact card falls back to it). */
    agent_company: string | null;
    broker_name: string | null;
    broker_phone: string | null;
    broker_office_address: string | null;
    broker_license_number: string | null;
}


let cachedPromise: Promise<FooterAgentInfo | null> | null = null;


function fetchFooterAgentInfo(): Promise<FooterAgentInfo | null> {
    if (!cachedPromise) {
        cachedPromise = apiClient
            .get<{ success: boolean; data: FooterAgentInfo }>('/agent-config/public')
            .then((resp) => resp.data?.data || null)
            .catch(() => null);
    }
    return cachedPromise;
}


export function useFooterAgentInfo(): FooterAgentInfo | null {
    const [info, setInfo] = useState<FooterAgentInfo | null>(null);
    useEffect(() => {
        let cancelled = false;
        fetchFooterAgentInfo().then((data) => {
            if (!cancelled) setInfo(data);
        });
        return () => { cancelled = true; };
    }, []);
    return info;
}
